import React, { useState } from 'react';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  Image, Video, Film, FileText, Mic, Type, Layout,
  Sparkles, Loader2, Download, Copy, RefreshCw,
  Palette, Wand2, Clock, Star, Grid, List, Share2, Save
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const contentTypes = [
  { id: 'image', label: 'AI Image', icon: Image, description: 'Generate stunning visuals' },
  { id: 'video', label: 'AI Video', icon: Video, description: 'Create short videos' },
  { id: 'reels', label: 'AI Reels', icon: Film, description: 'Vertical short content' },
  { id: 'brochure', label: 'AI Brochure', icon: Layout, description: 'Marketing materials' },
  { id: 'copy', label: 'AI Copywriting', icon: Type, description: 'Compelling ad copy' },
  { id: 'voiceover', label: 'AI Voiceover', icon: Mic, description: 'Professional narration' },
];

const tones = [
  'Professional', 'Casual', 'Exciting', 'Informative', 'Persuasive', 'Friendly', 'Urgent', 'Luxurious'
];

const generatedAssets = [
  { id: 1, type: 'image', title: 'Summer Sale Banner', time: '2 hours ago', thumbnail: '🖼️' },
  { id: 2, type: 'video', title: 'Product Showcase', time: '5 hours ago', thumbnail: '🎬' },
  { id: 3, type: 'reels', title: 'Behind the Scenes', time: '1 day ago', thumbnail: '📱' },
  { id: 4, type: 'copy', title: 'Email Campaign', time: '1 day ago', thumbnail: '✍️' },
  { id: 5, type: 'image', title: 'Instagram Post', time: '2 days ago', thumbnail: '🖼️' },
  { id: 6, type: 'brochure', title: 'Product Catalog', time: '3 days ago', thumbnail: '📄' },
];

const aiSuggestions = [
  'Summer sale post for Instagram with vibrant colors',
  'Product showcase video with modern aesthetic',
  'Behind-the-scenes reel for fashion brand',
  'Email copy for new collection launch',
  'LinkedIn post about industry trends',
];

export default function ContentStudio() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState('image');
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('Professional');
  const [brand, setBrand] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [aiCredits, setAiCredits] = useState({ used: 12450, total: 20000 });
  const [savingToLibrary, setSavingToLibrary] = useState(false);

  const generateContent = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description for your content');
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(`${API}/api/marketing/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          type: selectedType, 
          prompt, 
          tone, 
          brand 
        })
      });

      if (response.ok) {
        const data = await response.json();
        setGeneratedContent(data);
        toast.success('Content generated successfully!');
      } else {
        // Demo response
        setGeneratedContent({
          type: selectedType,
          content: selectedType === 'copy' 
            ? `✨ Introducing Our Summer Collection! ✨\n\nGet ready to turn heads this season with our exclusive new arrivals. From beach-ready looks to evening elegance, we've got everything you need to make this summer unforgettable.\n\n🔥 Limited Time Offer: 30% OFF on all new styles\n📦 Free shipping on orders over $50\n\nShop now and embrace the sunshine! ☀️\n\n#SummerStyle #NewCollection #Fashion`
            : 'Content preview would appear here',
          suggestions: [
            'Try adding more emotional appeal',
            'Consider shorter sentences for mobile',
            'Add a clear call-to-action'
          ]
        });
        toast.success('Content generated!');
      }
    } catch (error) {
      // Demo fallback
      setGeneratedContent({
        type: selectedType,
        content: selectedType === 'copy' 
          ? `✨ Introducing Our Summer Collection! ✨\n\nGet ready to turn heads this season with our exclusive new arrivals. From beach-ready looks to evening elegance, we've got everything you need to make this summer unforgettable.\n\n🔥 Limited Time Offer: 30% OFF on all new styles\n📦 Free shipping on orders over $50\n\nShop now and embrace the sunshine! ☀️\n\n#SummerStyle #NewCollection #Fashion`
          : 'Content preview would appear here',
      });
      toast.success('Content generated!');
    } finally {
      setGenerating(false);
    }
  };

  const copyContent = () => {
    if (generatedContent?.content) {
      navigator.clipboard.writeText(generatedContent.content);
      toast.success('Content copied to clipboard!');
    }
  };

  const saveToLibrary = async () => {
    if (!generatedContent) return;
    
    setSavingToLibrary(true);
    try {
      const response = await fetch(`${API}/api/marketing/content-library`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          type: generatedContent.type,
          title: prompt.slice(0, 50) || 'Untitled Content',
          content: generatedContent.content,
          image_base64: generatedContent.image_base64,
          prompt: prompt,
          tone: tone,
          brand: brand
        })
      });

      if (response.ok) {
        toast.success('Saved to Content Library!');
      } else {
        toast.error('Failed to save content');
      }
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSavingToLibrary(false);
    }
  };

  const useInPost = () => {
    // Store content in sessionStorage and navigate to Social Media
    sessionStorage.setItem('postContent', JSON.stringify({
      content: generatedContent?.content || '',
      image_base64: generatedContent?.image_base64 || null
    }));
    navigate('/social-media?createPost=true');
    toast.success('Opening Social Media to create post...');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">AI Content Studio</h1>
          <p className="text-muted-foreground">Create amazing content with the power of AI</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-secondary rounded-lg border border-border">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{aiCredits.used.toLocaleString()}/{aiCredits.total.toLocaleString()}</span>
            </div>
            <p className="text-xs text-muted-foreground">AI Credits</p>
          </div>
        </div>
      </div>

      {/* Content Type Selection */}
      <div className="marketing-card">
        <h3 className="font-semibold mb-4">Create New Content</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {contentTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              className={`
                p-4 rounded-xl border-2 text-center transition-all duration-200
                ${selectedType === type.id 
                  ? 'border-primary bg-primary/10' 
                  : 'border-border bg-secondary/30 hover:border-primary/50'}
              `}
            >
              <type.icon className={`w-8 h-8 mx-auto mb-2 ${selectedType === type.id ? 'text-primary' : 'text-muted-foreground'}`} />
              <p className={`text-sm font-medium ${selectedType === type.id ? 'text-primary' : ''}`}>{type.label}</p>
              <p className="text-xs text-muted-foreground mt-1">{type.description}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* AI Content Generator */}
        <div className="lg:col-span-2 space-y-4">
          <div className="marketing-card">
            <div className="flex items-center gap-2 mb-4">
              <Wand2 className="w-5 h-5 text-primary" />
              <h3 className="font-semibold">AI Content Generator</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">What do you want to create?</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={`E.g., Create a modern Instagram post for a summer sale of fashion collection with 30% off. Use vibrant colors and trendy design.`}
                  className="marketing-input h-32 resize-none"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">{prompt.length}/500</span>
                  <button 
                    onClick={() => setPrompt('')}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Content Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="marketing-select"
                  >
                    {contentTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tone of Voice</label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    className="marketing-select"
                  >
                    {tones.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Brand</label>
                  <input
                    type="text"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    placeholder="Your brand name"
                    className="marketing-input"
                  />
                </div>
              </div>

              <button
                onClick={generateContent}
                disabled={generating}
                className="marketing-btn-primary w-full flex items-center justify-center gap-2 h-12"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Content...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Content
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Generated Content Preview */}
          {generatedContent && (
            <div className="marketing-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Generated Content</h3>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={copyContent}
                    className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-foreground"
                    title="Copy"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={generateContent}
                    className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-foreground"
                    title="Regenerate"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  {generatedContent.image_base64 && (
                    <a 
                      href={`data:image/png;base64,${generatedContent.image_base64}`}
                      download={`generated-${selectedType}-${Date.now()}.png`}
                      className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-foreground"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  )}
                  <button 
                    onClick={saveToLibrary}
                    disabled={savingToLibrary}
                    className="p-2 hover:bg-secondary rounded-lg text-muted-foreground hover:text-foreground"
                    title="Save to Library"
                  >
                    {savingToLibrary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="p-4 bg-secondary/30 rounded-lg border border-border">
                {generatedContent.image_base64 ? (
                  <div className="space-y-4">
                    <img 
                      src={`data:image/png;base64,${generatedContent.image_base64}`}
                      alt="Generated content"
                      className="w-full rounded-lg"
                    />
                    <p className="text-sm text-muted-foreground">{generatedContent.content}</p>
                  </div>
                ) : selectedType === 'image' || selectedType === 'video' || selectedType === 'reels' || selectedType === 'brochure' ? (
                  <div className="space-y-4">
                    {generatedContent.content && generatedContent.content.includes('failed') ? (
                      <div className="text-center py-8">
                        <p className="text-red-400 text-sm">{generatedContent.content}</p>
                      </div>
                    ) : (
                      <div className="aspect-video bg-secondary rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          <Image className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground">{generatedContent.content}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap text-sm">{generatedContent.content}</div>
                )}
              </div>

              {generatedContent.suggestions && (
                <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                  <p className="text-sm font-medium text-primary mb-2">AI Suggestions:</p>
                  <ul className="space-y-1">
                    {generatedContent.suggestions.map((suggestion, index) => (
                      <li key={index} className="text-xs text-muted-foreground flex items-start gap-2">
                        <span className="text-primary">•</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={saveToLibrary}
                  disabled={savingToLibrary}
                  className="marketing-btn-secondary flex-1 flex items-center justify-center gap-2"
                >
                  {savingToLibrary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save to Library
                </button>
                <button
                  onClick={useInPost}
                  className="marketing-btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Use in Post
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="space-y-4">
          {/* AI Suggestions */}
          <div className="marketing-card">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-sm">AI Suggestions</h3>
            </div>
            <div className="space-y-2">
              {aiSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => setPrompt(suggestion)}
                  className="w-full p-3 text-left text-sm bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          {/* Generated Assets */}
          <div className="marketing-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-sm">Recent Generations</h3>
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className={viewMode === 'grid' ? 'grid grid-cols-2 gap-2' : 'space-y-2'}>
              {generatedAssets.slice(0, 6).map((asset) => (
                <div 
                  key={asset.id}
                  className={`
                    p-3 bg-secondary/50 rounded-lg cursor-pointer hover:bg-secondary transition-colors
                    ${viewMode === 'list' ? 'flex items-center gap-3' : ''}
                  `}
                >
                  <div className={`
                    ${viewMode === 'grid' ? 'text-2xl mb-2' : 'text-xl'}
                  `}>
                    {asset.thumbnail}
                  </div>
                  <div className={viewMode === 'list' ? 'flex-1' : ''}>
                    <p className="text-xs font-medium truncate">{asset.title}</p>
                    <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1">
                      <Clock className="w-3 h-3" />
                      {asset.time}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <button className="w-full mt-4 text-primary text-sm hover:underline">
              View All Assets →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
